import { MouseEvent, useState } from 'react';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormRichTextEditor from 'src/components/Control/FormControls/FormRichTextEditor';
import { FORM } from 'src/constants/Form';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import { setError } from 'src/redux/actions/errorsActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { postSettingsAction } from 'src/redux/actions/settingActions';
import OrganisationEmailsValidator from 'src/validations/OrganisationEmailsValidator';
import BottomRow from 'src/components/UI/BottomRow';
import InlineHelp from 'src/components/UI/InlineHelp';
import Line from 'src/components/UI/Line';

const OrganisationEmailsForm = () => {
  const dispatch = useAppDispatch();

  const settings = useSelector((state: Store) => state.settings);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const emailSettings = settings.find((s) => s.type === 'email')?.values?.[0] || {};

  const [formState, setFormState] = useState({
    receipt_email_title: emailSettings.receipt_email_title || '',
    receipt_email_message: emailSettings.receipt_email_message || '',
    invoice_email_title: emailSettings.invoice_email_title || '',
    invoice_email_message: emailSettings.invoice_email_message || '',
  });

  const onFieldChange = (value: Value, field: string) => {
    setFormState({
      ...formState,
      [field]: value,
    });
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.edit_organisation_emails]: {} }));
    try {
      OrganisationEmailsValidator(lang).validateSync(formState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'forms.organisation.validation_errors',
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.edit_organisation_emails]: errorBag }));
      return;
    }
    dispatch(postSettingsAction({
      type: 'email',
      values: [{
        receipt_email_title: formState.receipt_email_title,
        receipt_email_message: formState.receipt_email_message,
        invoice_email_title: formState.invoice_email_title,
        invoice_email_message: formState.invoice_email_message,
      }],
    }));
  };

  return (
    <div style={{ width: '640px', marginTop: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <FormTextField
          form={FORM.edit_organisation_emails}
          label={t(lang, 'forms.organisation.receipt_email_title')}
          name="receipt_email_title"
          onChange={onFieldChange}
          value={formState.receipt_email_title} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <FormRichTextEditor
          height="200px"
          form={FORM.edit_organisation_emails}
          label={t(lang, 'forms.organisation.receipt_email_message')}
          name="receipt_email_message"
          onChange={onFieldChange}
          value={formState.receipt_email_message} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <FormTextField
          form={FORM.edit_organisation_emails}
          label={t(lang, 'forms.organisation.invoice_email_title')}
          name="invoice_email_title"
          onChange={onFieldChange}
          value={formState.invoice_email_title} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <FormRichTextEditor
          height="200px"
          form={FORM.edit_organisation_emails}
          label={t(lang, 'forms.organisation.invoice_email_message')}
          name="invoice_email_message"
          onChange={onFieldChange}
          value={formState.invoice_email_message} />
      </div>
      <InlineHelp>
        <Line>{t(lang, 'forms.organisation.email_tags_help')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_first_name')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_last_name')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_company_name')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_amount')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_receipt_nb')}</Line>
        <Line>{t(lang, 'forms.organisation.tag_invoice_nb')}</Line>
        <Line>{t(lang, 'forms.organisation.email_tags_auto_replace')}</Line>
      </InlineHelp>
      <BottomRow>
        <Button
          variant="contained"
          color="primary"
          style={{ margin: '24px 0px 16px' }}
          onClick={(e) => handleSubmit(e)}>
          {t(lang, 'misc.save')}
        </Button>
      </BottomRow>
    </div>
  );
};

export default OrganisationEmailsForm;
